/* eslint-disable no-underscore-dangle */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {
  Text, FormErrorMessage, Image, Icon, Clamp,
} from 'components/atoms';
import { MediaUploadGallery } from 'containers'
import { useToggle } from 'hooks';
import { mediaType } from 'types'

const FieldImageGalleryUpload = ({
  title,
  field: {
    name, value
  }, // { name, value, onChange, onBlur }
  form: {
    touched,
    errors,
    setFieldValue,
  }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  onClick,
}) => {
  const [isHover, setHover] = useState(false);
  const [isOpen, toggle] = useToggle();

  const { alt, url, isLocal } = useMemo(() => ({
    alt: (value && value.alt) || '',
    url: (value && value.url) || '',
    isLocal: (value && value.isLocal) || (value && value.url) ? value.url.indexOf('http') === -1 : false
  }), [value])
  const error = useMemo(() => {
    const errorExists = touched[name] && errors[name];
    if (errorExists) return errors[name].url || errors[name].alt;
    return false;
  }, [touched, errors, name]);

  const hasImage = useMemo(() => url && url !== '', [url]);
  const hasAlt = useMemo(() => alt && alt !== '', [alt]);
  const computedText = useMemo(() => {
    if (!hasImage) return 'Selecionar imagem';
    if (!hasAlt) return 'Sem descrição';
    return alt;
  }, [hasAlt, alt, hasImage]);

  const handleClick = () => {
    onClick()
    toggle()
  };

  const handleChange = (img) => {
    setFieldValue(name, { ...img, isLocal: false })
  }

  return (
    <div>
      {title && <Text modifiers={['base']}>{title}</Text>}
      <MediaUploadGallery
        isOpen={isOpen}
        toggle={toggle}
        initialImage={value}
        onSubmit={handleChange}
      />
      <ImageInput
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={handleClick}
      >
        <ImageWrapper hasImage={hasImage} isHover={isHover}>
          {isHover && hasImage && (
            <ImageHover>
              <Icon height={16} color="#FFF" icon="edit" />
            </ImageHover>
          )}
          {hasImage ? (
            <Image src={url} alt={alt} isPortal={isLocal} height={36} />
          ) : (
            <Icon icon="add_photo" color="#FFF" height={20} />
          )}
        </ImageWrapper>
        {hasImage ? (
          <ImageInfo>
            <Text>{computedText}</Text>
            <Icon height={16} color="#000" icon="edit" onClick={handleChange} />
          </ImageInfo>
        ) : (
          <Text modifiers={['lighter']}>Selecionar imagem</Text>
        )}
      </ImageInput>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
      <InputSpacingFix />
    </div>
  );
}

const ImageInput = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: ${props => props.theme.values.borderSize} solid
    ${props => props.theme.colors.base};
  padding: ${props => `${props.theme.spacing.xs2} 0`};
  align-items: flex-start;
  cursor: pointer;
`;

// this spacing is used below the error message, this makes the margin feel more natural
// when the error happears and disappears
const InputSpacingFix = styled.div`
  height: ${props => props.theme.spacing.m};
  width: 100%;
`;


const ImageWrapper = styled.div`
  position: relative;
  height: 36px;
  width: calc(36px * 16 / 9);
  background-color: ${({ hasImage, isHover, theme }) => {
    if (hasImage) return theme.colors.bg;
    if (isHover && !hasImage) return theme.colors.primary;
    return theme.colors.bgDarker;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${props => props.theme.spacing.xs2};
  overflow: hidden;
`;

const ImageHover = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.bgHover};
`;

const ImageInfo = styled.div`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${props => props.lines};
  -webkit-box-orient: horizontal;
  gap: 12px;
`;

FieldImageGalleryUpload.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  field: PropTypes.shape({
    name: PropTypes.string,
    value: mediaType,
  }),
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    setFieldValue: PropTypes.func,
  }).isRequired,
}

FieldImageGalleryUpload.defaultProps = {
  onClick: () => null,
  field: {
    value: {
      title: '',
      alt: '',
      url: '',
      isLocal: true,
    },
  },
}

export default FieldImageGalleryUpload
